import React from "react";
// Components
import { TableBody, TableHead } from "./components";
// Hooks
import useActions from "hooks/useActions";
// Store Utils
import { useDispatch } from "react-redux";
// Utils
import getAllRowsData from "utils/getAllRowsData";
// Exteranl Tools
import { snakeCase } from "change-case";
// Styling
import "./Observations.scss";
// types
import { TableProps } from "./types";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import { filterKeys } from "Components/Users/Table";
import { reportsProfileSortingAction } from "store/filters/reducer.actions";
import { useParams } from "react-router-dom";

export const KEYS_TO_BE_REMOVED = ["id"];

const generatingDirectoryActions = (columns: any, rowsData: any) => {
  const fileDownload = async () => {
    if (rowsData.length === 0) {
      Toasts.error("Please select at least one report  from the table");
      return;
    }

    try {
      const res = await apiLibrary.file.fileDownload(
        "reviewer-observations",
        columns,
        rowsData?.map((item: any) => {
          return item?.observationCode?.id;
        })
      );

      // Convert the response to a Blob
      const blob = new Blob([res], { type: "application/octet-stream" });

      // Create a link element
      const link = document.createElement("a");

      // Set the href attribute to a URL created from the Blob
      link.href = window.URL.createObjectURL(blob);

      // Set the download attribute to specify the file name
      link.download = `observations_${new Date().toISOString()}.csv`;

      // Append the link to the body
      document.body.appendChild(link);

      // Programmatically click the link to trigger the download
      link.click();

      // Remove the link from the DOM
      document.body.removeChild(link);

      Toasts.success("Download complete");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error?.message;
      Toasts.error(errorMsg);
    }
  };
  const handleChangeRoute = () => {

  }
  return [
    {
      label: "Export Details",
      onClick: () => fileDownload(),
      divider: false,
      isVisible: true,
    },
    {
      label: "Analytics",
      onClick: () => handleChangeRoute(),
      divider: false,
      isVisible: true,
    },
  ];
};

export const Table: React.FC<TableProps> = ({
  table,
  columnOrder,
  columns,
  columnVisibility,
  flexRender,
  setSorting,
  sorting,
}) => {
  const {reportsId} = useParams<any>();
  const dispatch = useDispatch();
  const { reportsProfileActions } = useActions();
  const DirectoryActions = () => {
    const filteredKeys = filterKeys(columnVisibility, columnOrder);
    return generatingDirectoryActions(
      filteredKeys,
      getAllRowsData(table, columnOrder, columnVisibility, columns)
    );
  };

  const getReportsProfileActions = (data: any) => {
    
    const modalData = {
      status: data?.processingStatus,
      id: reportsId,
      versionId: data?.number,
      // processedReports: data.processedReports
      // path: "directory",
    };

    return reportsProfileActions(modalData);
  };

  const toggleSorting = (columnId: string) => {
    setSorting((currentSorting: any) => {
      const existingSort = currentSorting.find(
        (sort: any) => sort.columnId === columnId
      );
      const columnsId = snakeCase(columnId);
      if (!existingSort) {
        dispatch(reportsProfileSortingAction(columnsId, "asc"));

        return [{ columnId, type: "asc" }];
      } else if (existingSort.type === "asc") {
        dispatch(reportsProfileSortingAction(columnsId, "desc"));

        return [{ columnId, type: "desc" }];
      } else {
        dispatch(reportsProfileSortingAction("", ""));

        return [];
      }
    });
  };
  const handleClickOnTableColumnHeading = (header: any) =>
    header.column.getCanSort() && toggleSorting(header.column.id);

  // Comprehensive table initialization check
  if (!table) {
    return (
      <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
        <div className="flex items-center justify-center h-32">
          <p className="text-gray-500">Table not initialized...</p>
        </div>
      </div>
    );
  }

  // Check if table methods exist and are functions
  if (typeof table.getHeaderGroups !== 'function' || typeof table.getRowModel !== 'function') {
    return (
      <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
        <div className="flex items-center justify-center h-32">
          <p className="text-gray-500">Table methods not available...</p>
        </div>
      </div>
    );
  }

  // Additional safety check for table data
  try {
    const headerGroups = table.getHeaderGroups();
    const rowModel = table.getRowModel();
    
    if (!headerGroups || !rowModel) {
      return (
        <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
          <div className="flex items-center justify-center h-32">
            <p className="text-gray-500">Loading table data...</p>
          </div>
        </div>
      );
    }

    // Check if rows is an array
    if (!Array.isArray(rowModel.rows)) {
      return (
        <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
          <div className="flex items-center justify-center h-32">
            <p className="text-gray-500">Invalid table data structure...</p>
          </div>
        </div>
      );
    }
  } catch (error) {
    console.error('Table initialization error:', error);
    return (
      <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
        <div className="flex items-center justify-center h-32">
          <p className="text-red-500">Error loading table...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden max-h-[80vh] overflow-x-auto overflow-y-auto">
      <table className="w-full">
        <TableHead
          table={table}
          sorting={sorting}
          flexRender={flexRender}
          DirectoryActions={DirectoryActions()}
          handleClickOnColumnHead={handleClickOnTableColumnHeading}
        />
        <TableBody
          flexRender={flexRender}
          table={table}
          columnVisibility={columnVisibility}
          getReportsProfileActions={getReportsProfileActions}
        />
      </table>
    </div>
  );
};
